/**
 * The deal landscape — every open deal as a bubble. X = engagement score
 * (relative to the rest of the open pipeline), Y = deal-quality score,
 * bubble area = deal amount, color = health band. Deals with no amount yet
 * (pre-SQ) render at a fixed minimum size so they stay visible without
 * pretending to have a value.
 *
 * Clicking a bubble selects the deal for the detail panel beside the chart.
 */
import { useMemo } from "react";
import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { DealHealthBand, DealHealthDeal } from "../api";
import { fmtCents } from "../format";
import { BAND_LABEL, DEAL_HEALTH_COLORS, DEAL_HEALTH_GRID } from "./dealHealthColors";

interface BubblePoint {
  x: number;
  y: number;
  z: number;
  deal: DealHealthDeal;
}

const BANDS: DealHealthBand[] = ["stalled", "at_risk", "healthy"];

function BubbleTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: BubblePoint }[];
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  const { deal } = point;
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="font-medium text-foreground">{deal.name}</div>
      <div className="mt-1 space-y-0.5 text-muted-foreground">
        <div>{deal.amount_cents > 0 ? fmtCents(deal.amount_cents) : "No amount yet"}</div>
        <div>
          Engagement {Math.round(deal.scores.engagement)} · Quality{" "}
          {Math.round(deal.scores.deal_quality)}
        </div>
        <div>
          {BAND_LABEL[deal.band]}
          {deal.band_reasons.length > 0 && ` — ${deal.band_reasons.join("; ")}`}
        </div>
      </div>
    </div>
  );
}

export function DealLandscape({
  deals,
  selectedId,
  onSelect,
}: {
  deals: DealHealthDeal[];
  selectedId: string | null;
  onSelect: (deal: DealHealthDeal) => void;
}) {
  // One Scatter series per band so each gets its own fill; stalled/at-risk
  // draw first so healthy bubbles sit on top in overlaps.
  const series = useMemo(() => {
    const byBand: Record<DealHealthBand, BubblePoint[]> = {
      healthy: [],
      at_risk: [],
      stalled: [],
    };
    for (const d of deals) {
      byBand[d.band].push({
        x: d.scores.engagement,
        y: d.scores.deal_quality,
        // ZAxis maps value → bubble AREA. Zero-amount deals get a floor so
        // they render; everything else scales with deal size.
        z: Math.max(d.amount_cents / 100, 2_000),
        deal: d,
      });
    }
    return byBand;
  }, [deals]);

  return (
    // h-full + the flex column let the card stretch to the grid row's
    // height — the detail panel beside it is usually taller, and matching
    // it keeps the two cards bottom-aligned. The chart absorbs the extra
    // space via flex-1 below.
    <Card className="flex h-full flex-col">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
        <CardTitle className="text-sm">Deal landscape</CardTitle>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {BANDS.slice()
            .reverse()
            .map((b) => (
              <span key={b} className="flex items-center gap-1">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: DEAL_HEALTH_COLORS[b] }}
                />
                {BAND_LABEL[b]}
              </span>
            ))}
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <p className="mb-2 text-xs text-muted-foreground">
          Bubble size = deal amount · click a bubble for the breakdown
        </p>
        <div className="min-h-[420px] flex-1">
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 16, left: 8 }}>
              <CartesianGrid stroke={DEAL_HEALTH_GRID} strokeDasharray="2 3" />
              <XAxis
                type="number"
                dataKey="x"
                domain={[0, 100]}
                tick={{ fontSize: 10 }}
                label={{
                  value: "Engagement →",
                  position: "insideBottom",
                  offset: -8,
                  fontSize: 11,
                }}
              />
              <YAxis
                type="number"
                dataKey="y"
                domain={[0, 100]}
                tick={{ fontSize: 10 }}
                width={40}
                label={{
                  value: "Deal quality →",
                  angle: -90,
                  position: "insideLeft",
                  fontSize: 11,
                }}
              />
              <ZAxis type="number" dataKey="z" range={[60, 1400]} />
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                content={<BubbleTooltip />}
              />
              {BANDS.map((band) => (
                <Scatter
                  key={band}
                  name={BAND_LABEL[band]}
                  data={series[band]}
                  fill={DEAL_HEALTH_COLORS[band]}
                  fillOpacity={0.65}
                  stroke={DEAL_HEALTH_COLORS[band]}
                  onClick={(point: BubblePoint) => onSelect(point.deal)}
                  cursor="pointer"
                  shape={(props: unknown) => {
                    // Ring the selected deal's bubble so it's findable after
                    // clicking from the table.
                    const p = props as BubblePoint & {
                      cx?: number;
                      cy?: number;
                      size?: number;
                    };
                    const r = Math.sqrt((p.size ?? 60) / Math.PI);
                    const isSel = p.deal.hubspot_deal_id === selectedId;
                    return (
                      <g>
                        <circle
                          cx={p.cx}
                          cy={p.cy}
                          r={r}
                          fill={DEAL_HEALTH_COLORS[band]}
                          fillOpacity={0.65}
                          stroke={isSel ? "hsl(222 47% 11%)" : DEAL_HEALTH_COLORS[band]}
                          strokeWidth={isSel ? 2 : 1}
                        />
                      </g>
                    );
                  }}
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
